import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import Brand from "@/components/common/Brand";
import api, { formatError } from "@/lib/api";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="forgot-password-page">
      <div className="h-14 bg-[#0A0A0A] flex items-center justify-between px-4">
        <button data-testid="forgot-back-btn" onClick={() => navigate("/")} className="text-white/70 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
        <div className="flex items-baseline">
          <Brand className="text-xl" />
        </div>
        <div className="w-5" />
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 max-w-md mx-auto w-full">
        <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">{t("forgot_password.title")}</h1>
        <p className="text-sm text-[#999] mt-1">{t("forgot_password.email")}</p>

        {error && <InfoCard variant="danger" size="sm">{error}</InfoCard>}
        {sent && (
          <InfoCard variant="success" size="sm">{t("forgot_password.sent_hint")}</InfoCard>
        )}

        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("forgot_password.email")}</label>
            <input
              data-testid="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full h-12 px-4 rounded-full border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              placeholder="name@example.com"
              required
            />
          </div>
          <button
            data-testid="forgot-submit"
            type="submit"
            disabled={loading || sent}
            className="h-12 rounded-full bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50"
          >
            {loading ? t("forgot_password.submitting") : t("forgot_password.submit")}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-[#999]">
          <Link to="/login" data-testid="back-to-login" className="text-[#0A0A0A] font-medium hover:underline">{t("forgot_password.back_login")}</Link>
        </p>
      </div>
    </div>
  );
}
